import type { ReactNode } from "react";
import { CAMADA_PERFIS, STATUS_LABELS } from "@/types/editor-territorial";
import type { CamadaGeo } from "@/types/editor-territorial";
import type { AreaPiloto, Quadra } from "@/types/territorial";

interface SessionState {
  projetoId: string;
  areaPilotoId: number | null;
  quadraId: number | null;
}

interface LayerPanelProps {
  session: SessionState;
  onSessionChange: (patch: Partial<SessionState>) => void;
  onGenerateProjetoId: () => void;
  areasPiloto: AreaPiloto[];
  areasPilotoLoading: boolean;
  quadras: Quadra[];
  quadrasLoading: boolean;
  selectedQuadra: Quadra | null;
  linkedInspectionsCount: number;
  camadas: CamadaGeo[];
  camadasLoading: boolean;
  activeCamadaDestino: string | null;
  onSelectPerfil: (perfil: (typeof CAMADA_PERFIS)[number]) => void;
  hiddenCamadas: Set<string>;
  onToggleVisibility: (camadaDestino: string) => void;
  lockedCamadas: Set<string>;
  onToggleLock: (camadaDestino: string) => void;
  layerOpacity: number;
  onOpacityChange: (value: number) => void;
  statusFilter: string | null;
  onStatusFilterChange: (status: string | null) => void;
  /** Secao de camadas so de leitura (exemplares do CTA), acima das camadas de edicao. */
  referenceSection?: ReactNode;
}

export default function LayerPanel({
  session,
  onSessionChange,
  onGenerateProjetoId,
  areasPiloto,
  areasPilotoLoading,
  quadras,
  quadrasLoading,
  selectedQuadra,
  linkedInspectionsCount,
  camadas,
  camadasLoading,
  activeCamadaDestino,
  onSelectPerfil,
  hiddenCamadas,
  onToggleVisibility,
  lockedCamadas,
  onToggleLock,
  layerOpacity,
  onOpacityChange,
  statusFilter,
  onStatusFilterChange,
  referenceSection,
}: LayerPanelProps) {
  return (
    <div className="w-64 shrink-0 border-r border-background-200/70 bg-white overflow-y-auto flex flex-col">
      <div className="p-3 border-b border-background-100">
        <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-2">Sessão de edição</p>

        <label className="text-[11px] text-foreground-500 block mb-1">Projeto (sessão, UUID)</label>
        <div className="flex gap-1 mb-2">
          <input
            value={session.projetoId}
            onChange={(e) => onSessionChange({ projetoId: e.target.value })}
            placeholder="uuid do projeto"
            className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-background-200 rounded-md font-mono"
          />
          <button
            onClick={onGenerateProjetoId}
            title="Gerar novo UUID de sessão"
            className="px-2 py-1.5 rounded-md border border-background-200 hover:bg-background-50 cursor-pointer"
          >
            <i className="ri-refresh-line text-xs text-foreground-500" />
          </button>
        </div>
        <p className="text-[10px] text-foreground-400 mb-3">
          Sem camada operacional de projeto por UUID ainda — gerado por sessão de edição.
        </p>

        <label className="text-[11px] text-foreground-500 block mb-1">Área piloto</label>
        <select
          value={session.areaPilotoId ?? ""}
          onChange={(e) => onSessionChange({ areaPilotoId: e.target.value ? Number(e.target.value) : null, quadraId: null })}
          className="w-full text-xs px-2 py-1.5 border border-background-200 rounded-md mb-2"
        >
          <option value="">{areasPilotoLoading ? "Carregando..." : "Selecione..."}</option>
          {areasPiloto.map((area) => (
            <option key={area.id} value={area.id}>
              {area.name} {area.code ? `(${area.code})` : ""}
              {area.code === "SBV-DEMO-001" ? " — demo, não usar como limite real" : ""}
            </option>
          ))}
        </select>

        <label className="text-[11px] text-foreground-500 block mb-1">Quadra operacional</label>
        <select
          value={session.quadraId ?? ""}
          onChange={(e) => onSessionChange({ quadraId: e.target.value ? Number(e.target.value) : null })}
          disabled={!session.areaPilotoId}
          className="w-full text-xs px-2 py-1.5 border border-background-200 rounded-md disabled:bg-background-50 disabled:text-foreground-300"
        >
          <option value="">
            {!session.areaPilotoId ? "Selecione uma área piloto primeiro" : quadrasLoading ? "Carregando..." : "Selecione..."}
          </option>
          {quadras.map((quadra) => (
            <option key={quadra.id} value={quadra.id}>
              {quadra.code} {quadra.name ? `— ${quadra.name}` : ""}
            </option>
          ))}
        </select>

        {session.areaPilotoId && !quadrasLoading && quadras.length === 0 && (
          <p className="text-[10px] text-caution-700 bg-caution-50 rounded px-2 py-1 mt-1.5">
            Nenhuma quadra operacional promovida para esta área piloto ainda.
          </p>
        )}

        {selectedQuadra && (
          <div className="mt-2 text-[11px] bg-background-50 rounded-md p-2 space-y-1">
            <div className="flex justify-between">
              <span className="text-foreground-400">Quadra id</span>
              <span className="font-mono text-foreground-700">#{selectedQuadra.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-400">Área</span>
              <span className="text-foreground-700">{selectedQuadra.areaM2 ? `${Math.round(selectedQuadra.areaM2)} m²` : "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-400">Inspeções CDIT vinculadas</span>
              <span className="text-foreground-700">{linkedInspectionsCount}</span>
            </div>
            {!selectedQuadra.geom && (
              <p className="text-caution-700">Sem geometria — não é possível dar zoom nesta quadra.</p>
            )}
          </div>
        )}
      </div>

      {referenceSection}

      <div className="p-3 border-b border-background-100">
        <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-2">Camadas rápidas</p>
        <div className="grid grid-cols-2 gap-1.5">
          {CAMADA_PERFIS.map((perfil) => (
            <button
              key={perfil.camadaDestino}
              onClick={() => perfil.disponivel && onSelectPerfil(perfil)}
              disabled={!perfil.disponivel}
              title={perfil.disponivel ? perfil.label : `${perfil.label} (ainda sem camada de destino no backend)`}
              className={`text-[11px] px-2 py-1.5 rounded-md text-left transition-colors ${
                !perfil.disponivel
                  ? "text-foreground-300 bg-background-50 cursor-not-allowed"
                  : activeCamadaDestino === perfil.camadaDestino
                    ? "bg-primary-500 text-white cursor-pointer"
                    : "bg-background-50 text-foreground-700 hover:bg-background-100 cursor-pointer"
              }`}
            >
              {perfil.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3 border-b border-background-100 flex-1">
        <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-2">Camadas publicadas</p>
        {camadasLoading && <p className="text-[11px] text-foreground-400">Carregando...</p>}
        <div className="space-y-1">
          {camadas.map((camada) => (
            <div
              key={camada.id}
              className="flex items-center justify-between text-[11px] px-1.5 py-1 rounded-md hover:bg-background-50"
            >
              <span className="text-foreground-700 truncate">{camada.nome}</span>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => onToggleLock(camada.camadaDestino)}
                  title={lockedCamadas.has(camada.camadaDestino) ? "Desbloquear edição" : "Bloquear edição"}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer"
                >
                  <i
                    className={`text-xs ${
                      lockedCamadas.has(camada.camadaDestino) ? "ri-lock-line text-danger-500" : "ri-lock-unlock-line text-foreground-400"
                    }`}
                  />
                </button>
                <button
                  onClick={() => onToggleVisibility(camada.camadaDestino)}
                  title={hiddenCamadas.has(camada.camadaDestino) ? "Mostrar camada" : "Ocultar camada"}
                  className="w-5 h-5 flex items-center justify-center rounded hover:bg-background-100 cursor-pointer"
                >
                  <i
                    className={`text-xs ${
                      hiddenCamadas.has(camada.camadaDestino) ? "ri-eye-off-line text-foreground-400" : "ri-eye-line text-foreground-600"
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3">
          <label className="text-[11px] text-foreground-500 block mb-1">Opacidade das feições ({Math.round(layerOpacity * 100)}%)</label>
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.1}
            value={layerOpacity}
            onChange={(e) => onOpacityChange(Number(e.target.value))}
            className="w-full"
          />
        </div>
      </div>

      <div className="p-3">
        <p className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wide mb-2">Filtrar por status</p>
        <select
          value={statusFilter ?? ""}
          onChange={(e) => onStatusFilterChange(e.target.value || null)}
          className="w-full text-xs px-2 py-1.5 border border-background-200 rounded-md"
        >
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export type { SessionState };
